import * as apicall from "../components/Apicall";
import { EMPTY_MODULE_ACCESS } from "../constants/platformModules";

// Platform API client (/api/v1/platform): module access, Super Admin enablement,
// the unified approvals inbox (HR + Finance + DMS) and approval hand-overs.
// Same conventions as hrmsService.js: writes are POST, ids are the encrypted
// strings the API returned, apicall.get throws on non-2xx, apicall.post resolves
// with { success:false, message }.

export { responseMessage, unwrap } from "./hrmsService";

export const qs = (params = {}) => {
  const p = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "");
  return p.length ? "?" + p.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join("&") : "";
};

/* ---------------- access (menus and gates) ---------------- */
export const getAccess = async () => {
  try {
    const res = await apicall.get("platform/access");
    return res?.data || EMPTY_MODULE_ACCESS;
  } catch (err) {
    // Not signed in / API down / platform SQL not run yet: treat as "nothing on", never break the shell.
    return EMPTY_MODULE_ACCESS;
  }
};

/* ---------------- Super Admin: switch Finance / DMS per vendor ---------------- */
// fresh: add a changing query so no cache in the browser or in front of the API can answer with the state from before a switch.
export const getEnablement = (vendorId, fresh = false) => apicall.get(`platform/admin/enablement/${vendorId}${fresh ? `?_=${Date.now()}` : ""}`);
export const setFeatureEnabled = (vendorId, module, featureKey, isEnabled) =>
  apicall.post({ module, feature_key: featureKey, is_enabled: isEnabled }, `platform/admin/enablement/${vendorId}`);

/* ---------------- unified approvals ---------------- */
// Items carry `source` (HR | PLATFORM); decisions go back to the same source.
export const getInbox = (history) => apicall.get(`platform/approvals/inbox${qs({ history: history ? 1 : undefined })}`);
export const getApprovalCount = () => apicall.get("platform/approvals/count");
export const getMyRequests = () => apicall.get("platform/approvals/mine");
export const getApproval = (source, id) => apicall.get(`platform/approvals/${source}/${id}`);
export const actOnApproval = (source, id, action, comment) => apicall.post({ action, comment }, `platform/approvals/${source}/${id}/action`);
export const withdrawApproval = (source, id) => apicall.post({}, `platform/approvals/${source}/${id}/withdraw`);
// items: [{ source, request_id }]
export const bulkApprovalAction = (items, action, comment) => apicall.post({ items, action, comment }, "platform/approvals/bulk-action");

/* ---------------- hand-overs (shared with HR: one setting covers every module) ---------------- */
export const listDelegations = () => apicall.get("platform/delegations");
export const createDelegation = (data) => apicall.post(data, "platform/delegations");
export const cancelDelegation = (id) => apicall.del(`platform/delegations/${id}`);

/* ---------------- module setup shared by Finance and DMS ---------------- */
// `module` is "fin" or "dms" (the URL prefix).
export const getPermissionCatalog = (module) => apicall.get(`${module}/setup/permissions/catalog`);
export const listPermissions = (module) => apicall.get(`${module}/setup/permissions`);
export const grantPermission = (module, data) => apicall.post(data, `${module}/setup/permissions`);
export const revokePermission = (module, id) => apicall.del(`${module}/setup/permissions/${id}`);

export const listDocSequences = (module) => apicall.get(`${module}/setup/doc-sequences`);
export const saveDocSequence = (module, data) => apicall.post(data, `${module}/setup/doc-sequences`);
export const previewDocSequence = (module, data) => apicall.post(data, `${module}/setup/doc-sequences/preview`);

export const getWorkflowMeta = (module) => apicall.get(`${module}/setup/workflows/meta`);
export const listWorkflows = (module) => apicall.get(`${module}/setup/workflows`);
export const saveWorkflow = (module, id, data) => apicall.post(data, id ? `${module}/setup/workflows/${id}` : `${module}/setup/workflows`);
export const deactivateWorkflow = (module, id) => apicall.del(`${module}/setup/workflows/${id}`);
